/**
 * FinanceExpenses — live expense list from /v1/finance/expenses with status +
 * category filters, inline approve/reject (/v1/finance/expenses/{id}/...), and
 * a create modal (CreateEmployeeModal idiom).
 *
 * P1: colors/sizes flow from src/theme/tokens.ts (canonical P1 design system).
 */
import { useState, useEffect } from 'react'
import { createPortal } from 'react-dom'
import { Check, X, Plus } from 'lucide-react'
import { SectionTitle, LoadingState, ErrorState, EmptyState } from '../../DashboardShared'
import { CurrencySelect } from '../../CurrencySelect'
import { useAsync, fetchExpenses, approveExpense, rejectExpense, createExpense } from '../../api'
import type { FinanceExpense, CreateExpenseRequest } from '../../types'
import { apiErrorMessage } from '../../../../api/client'
import { money } from '../../../../lib/money'
import { toast } from 'sonner'
import { useWriteAccess } from '../../../../lib/writeAccess'
import { ink, overlay, statusExpense, surface } from '../../../../theme/tokens'

const STATUS_BADGE: Record<string, { bg: string; color: string }> = {
  pending:  { bg: statusExpense.pending.bg,  color: statusExpense.pending.text },
  approved: { bg: statusExpense.approved.bg, color: statusExpense.approved.text },
  rejected: { bg: statusExpense.rejected.bg, color: statusExpense.rejected.text },
  paid:     { bg: statusExpense.paid.bg,     color: statusExpense.paid.text },
}

function StatusBadge({ status }: { status: string }) {
  const cfg = STATUS_BADGE[status] ?? { bg: surface.panel, color: ink.secondary }
  return (
    <span className="k-badge" style={{ background: cfg.bg, color: cfg.color }}>
      <span className="k-badge-dot" />{status.toUpperCase()}
    </span>
  )
}

const STATUSES = ['all', 'pending', 'approved', 'rejected', 'paid'] as const

// The 0038 doc-comment value set. Nothing else enforces it (no CHECK, no
// module validation) — this select IS the enforcement. Exported: the Overview
// category chart binds each category's palette slot to its position HERE
// (slice 4 — the fixed-slot chart rule), so the vocabulary lives once.
export const CATEGORIES = ['travel', 'software', 'office', 'marketing', 'other'] as const

const inputCls = 'w-full h-10 px-3 text-13 bg-surface-control border border-edge-control rounded-control text-ink-body placeholder:text-ink-muted focus:outline-none focus:border-accent'
const labelCls = 'block text-11 font-semibold uppercase tracking-overline text-ink-muted mb-1'

// Newest first by the date that matters: the expense's own date, falling back
// to creation time — a backdated entry lands in its true position, not on top.
function sortByExpenseDate(list: FinanceExpense[]): FinanceExpense[] {
  return [...list].sort((a, b) =>
    new Date(b.expenseDate ?? b.createdAt).getTime() - new Date(a.expenseDate ?? a.createdAt).getTime())
}

function today(): string {
  return new Date().toISOString().slice(0, 10)
}

// ── Create modal (CreateEmployeeModal idiom) ──────────────────────────────────

function CreateExpenseModal({ sessionUser, onClose, onCreated }: {
  sessionUser: string | null
  onClose: () => void
  onCreated: (e: FinanceExpense) => void
}) {
  const [description, setDescription] = useState('')
  const [amount, setAmount] = useState('')
  // Increment-3 picker: expenses default GBP (a cost, not seeded from the
  // tenant's ISSUING default); the server captures-or-refuses on submit.
  const [currency, setCurrency] = useState('GBP')
  const [category, setCategory] = useState('')
  const [expenseDate, setExpenseDate] = useState(today())
  const [receiptUrl, setReceiptUrl] = useState('')
  const [notes, setNotes] = useState('')
  const [submittedBy, setSubmittedBy] = useState(sessionUser ?? '')
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    document.addEventListener('keydown', handler)
    return () => document.removeEventListener('keydown', handler)
  }, [onClose])

  const amountNum = Number(amount)
  const valid = description.trim() !== '' &&
    amount !== '' && Number.isFinite(amountNum) && amountNum > 0 &&
    category !== '' && expenseDate !== '' && submittedBy.trim() !== ''

  async function submit() {
    setBusy(true)
    try {
      const body: CreateExpenseRequest = {
        description: description.trim(),
        amount: Math.round(amountNum * 100) / 100,
        currency,
        category,
        submittedBy: submittedBy.trim(),
        receiptUrl: receiptUrl.trim() || undefined,
        notes: notes.trim() || undefined,
        // Go *time.Time is strict RFC3339 — a bare YYYY-MM-DD fails to decode.
        expenseDate: `${expenseDate}T00:00:00Z`,
      }
      const created = await createExpense(body)
      toast.success('Expense added')
      onCreated(created)
    } catch (e) {
      toast.error(apiErrorMessage(e, 'Creating the expense failed'))
    } finally {
      setBusy(false)
    }
  }

  return createPortal(
    <div style={{ position: 'fixed', inset: 0, zIndex: 9999, background: overlay.scrim, backdropFilter: 'blur(2px)' }}
      className="flex items-center justify-center p-4"
      onClick={e => { if (e.target === e.currentTarget) onClose() }}>
      {/* S-Tier (slice 4): a centered dialog is a floating widget — .k-card
          (the CloseDealModal precedent). NOTE it is a MODAL, not a drawer:
          the CreateEmployeeModal idiom this form was built on. */}
      <div role="dialog" aria-modal="true" aria-label="New expense"
        className="k-card w-full max-w-md p-6 gap-4">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-detail-title font-bold text-ink-primary">New expense</div>
            <div className="text-12 text-ink-muted mt-0.5">Created pending — approval is a separate act.</div>
          </div>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-control text-ink-muted hover:bg-surface-nav-hover transition-colors flex-shrink-0">
            <X size={16} />
          </button>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2">
            <label htmlFor="new-exp-desc" className={labelCls}>Description</label>
            <input id="new-exp-desc" value={description} onChange={e => setDescription(e.target.value)} placeholder="Team travel to client site" className={inputCls} />
          </div>
          <div>
            <label htmlFor="new-exp-amount" className={labelCls}>Amount ({currency})</label>
            <input id="new-exp-amount" type="number" min={0.01} step={0.01} value={amount} onChange={e => setAmount(e.target.value)} placeholder="42.50" className={inputCls} />
          </div>
          <div>
            <label htmlFor="new-exp-currency" className={labelCls}>Currency</label>
            <CurrencySelect id="new-exp-currency" className={inputCls} value={currency} onChange={setCurrency} />
          </div>
          <div>
            <label htmlFor="new-exp-category" className={labelCls}>Category</label>
            <select id="new-exp-category" value={category} onChange={e => setCategory(e.target.value)} className={`${inputCls} capitalize`}>
              <option value="" disabled>Select…</option>
              {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div>
            <label htmlFor="new-exp-date" className={labelCls}>Expense date</label>
            <input id="new-exp-date" type="date" value={expenseDate} onChange={e => setExpenseDate(e.target.value)} className={inputCls} />
          </div>
          <div>
            <label htmlFor="new-exp-by" className={labelCls}>Submitted by</label>
            <input id="new-exp-by" value={submittedBy} onChange={e => setSubmittedBy(e.target.value)} placeholder="Your name" className={inputCls} />
          </div>
          <div className="col-span-2">
            <label htmlFor="new-exp-receipt" className={labelCls}>Receipt URL <span className="font-normal normal-case tracking-normal text-ink-muted">(optional)</span></label>
            <input id="new-exp-receipt" type="url" value={receiptUrl} onChange={e => setReceiptUrl(e.target.value)} placeholder="https://…" className={inputCls} />
          </div>
          <div className="col-span-2">
            <label htmlFor="new-exp-notes" className={labelCls}>Notes <span className="font-normal normal-case tracking-normal text-ink-muted">(optional)</span></label>
            <textarea id="new-exp-notes" rows={2} value={notes} onChange={e => setNotes(e.target.value)} maxLength={2000}
              className="w-full px-3 py-2 text-13 leading-relaxed bg-surface-control border border-edge-control rounded-control text-ink-body placeholder:text-ink-muted focus:outline-none focus:border-accent resize-y" />
          </div>
        </div>

        <div className="flex items-center gap-2">
          <button onClick={submit} disabled={busy || !valid}
            className="k-btn k-btn--lg flex-1 inline-flex items-center justify-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed">
            <Check size={14} /> {busy ? 'Adding…' : 'Add expense'}
          </button>
          <button onClick={onClose} disabled={busy}
            className="k-btn-secondary flex-1 disabled:opacity-40">
            Cancel
          </button>
        </div>
      </div>
    </div>,
    document.body
  )
}

// ── Main page ─────────────────────────────────────────────────────────────────

export default function FinanceExpenses({ sessionUser = null }: { sessionUser?: string | null }) {
  const { data, loading, error } = useAsync(fetchExpenses, [])
  const [expenses, setExpenses] = useState<FinanceExpense[]>([])
  const [statusFilter, setStatusFilter] = useState<(typeof STATUSES)[number]>('all')
  const [category, setCategory] = useState('')
  const [creating, setCreating] = useState(false)
  // Expense create / approve / reject are records-level finance writes
  // (lib/writeAccess; fail closed).
  const { records } = useWriteAccess()

  useEffect(() => { if (data) setExpenses(data) }, [data])

  if (loading) return <LoadingState />
  if (error) return <ErrorState message={error} />

  const categories = Array.from(new Set(expenses.map(e => e.category).filter(Boolean))) as string[]
  const filtered = expenses.filter(e => {
    if (statusFilter !== 'all' && e.status !== statusFilter) return false
    if (category && e.category !== category) return false
    return true
  })

  async function decide(id: string, action: 'approve' | 'reject') {
    try {
      const updated = action === 'approve' ? await approveExpense(id) : await rejectExpense(id)
      setExpenses(prev => prev.map(e => e.id === id ? updated : e))
      toast.success(action === 'approve' ? 'Expense approved' : 'Expense rejected')
    } catch (e) {
      toast.error(apiErrorMessage(e, 'Action failed'))
    }
  }

  return (
    <div className="page-enter flex flex-col gap-4">
      <SectionTitle
        title="Expenses"
        subtitle={`${filtered.length} expense${filtered.length !== 1 ? 's' : ''}`}
        action={records ? (
          <button onClick={() => setCreating(true)}
            className="k-btn k-btn--lg inline-flex items-center gap-2">
            <Plus size={14} /> New expense
          </button>
        ) : undefined}
      />

      <div className="flex items-center gap-2 flex-wrap">
        {STATUSES.map(s => (
          <button key={s} onClick={() => setStatusFilter(s)}
            className={`px-3 py-2 rounded-full text-12 font-medium transition-colors capitalize ${statusFilter === s ? 'bg-ink-primary text-ink-on-accent border border-ink-primary' : 'bg-surface-control text-ink-secondary hover:bg-surface-nav-hover border border-edge-control hover:border-edge-control-hover'}`}>
            {s === 'all' ? 'All' : s}
          </button>
        ))}
        {categories.length > 0 && (
          <select value={category} onChange={e => setCategory(e.target.value)}
            className="ml-auto h-8 px-2 text-12 bg-surface-control border border-edge-control rounded-control text-ink-body focus:outline-none focus:border-accent capitalize">
            <option value="">All categories</option>
            {categories.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        )}
      </div>

      <div className="k-card">
        {filtered.length === 0 ? (
          <EmptyState message="No expenses match your filter" />
        ) : (
          <table className="closr-table finance-table w-full">
            <thead>
              <tr>
                <th className="text-left">Description</th>
                <th className="text-left">Amount</th>
                <th className="text-left">Category</th>
                <th className="text-left">Submitted By</th>
                <th className="text-left">Date</th>
                <th className="text-left">Status</th>
                <th className="text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(e => (
                <tr key={e.id}>
                  <td className="font-medium text-ink-primary">{e.description}</td>
                  <td className="font-semibold text-ink-primary">{money(e.amount ?? 0, e.currency)}</td>
                  <td><span className="k-badge bg-surface-panel text-ink-secondary">{(e.category || 'other').toUpperCase()}</span></td>
                  <td className="text-12 text-ink-secondary">{e.submittedBy || '—'}</td>
                  <td className="text-12 text-ink-muted">{e.expenseDate ? new Date(e.expenseDate).toLocaleDateString('en-GB') : '—'}</td>
                  <td><StatusBadge status={e.status} /></td>
                  <td className="text-right">
                    {e.status === 'pending' ? (records &&
                      <div className="flex items-center justify-end gap-1">
                        <button onClick={() => decide(e.id, 'approve')} title="Approve"
                          className="p-2 rounded hover:bg-success-bg text-success transition-colors"><Check size={14} /></button>
                        <button onClick={() => decide(e.id, 'reject')} title="Reject"
                          className="p-2 rounded hover:bg-danger-bg text-danger transition-colors"><X size={14} /></button>
                      </div>
                    ) : (
                      <span className="text-11 text-ink-muted">{e.approvedBy ? `by ${e.approvedBy}` : '—'}</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {creating && records && (
        <CreateExpenseModal
          sessionUser={sessionUser}
          onClose={() => setCreating(false)}
          onCreated={created => {
            setCreating(false)
            setExpenses(prev => sortByExpenseDate([created, ...prev]))
          }}
        />
      )}
    </div>
  )
}
